/**
 * StrategyPickerPill — top-of-Command-Center dropdown that surfaces the
 * active deal strategy and lets the operator swap it live.
 *
 * Sits next to the Active Property Pill in the header. Reads the strategy
 * registry once, hydrates the current strategy from the property, and
 * writes back via PATCH so the entire pipeline + AI SDR pick up the new
 * frame within the same session.
 *
 * Data flow:
 *   GET  /api/v3/deal-strategies                    → static registry
 *   POST /api/v3/properties/{id}/strategies         → all calcs (needs inputs body)
 *   PATCH /api/v3/properties/{id}/strategy          → pin one
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronDown, Sparkles, Check, Loader2, Calculator, DollarSign } from "lucide-react";

// Strategies that let the operator type a single dollar amount inline in
// the picker instead of the "just save" happy path. Keeps the pill fast
// while still giving the "back-solve from $25k profit" moment the app
// was designed for.
const INLINE_DOLLAR_INPUT = {
  fixed_profit: {
    field: "target_profit_dollar",
    placeholder: "25000",
    hint: "Type the buyer's required profit — back-solves MAO",
    default: "25000",
  },
};

export default function StrategyPickerPill({ propertyId, dealType = "house", onChange }) {
  const [registry, setRegistry] = useState([]);
  const [active, setActive] = useState(null);          // {key, label, max_offer, ...}
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(null);
  const anchorRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  // Load registry + current property calcs.
  const load = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const [regRes, calcRes] = await Promise.all([
        api.get("/v3/deal-strategies"),
        api.post(`/v3/properties/${propertyId}/strategies`, { inputs: {} }),
      ]);
      setRegistry(regRes.data.strategies || []);
      const activeKey = calcRes.data.active_strategy_key;
      const activeRow = (calcRes.data.results || []).find((r) => r.key === activeKey)
        || calcRes.data.results?.[0]
        || null;
      setActive(activeRow);
    } catch (err) {
      // Silent fail — the pill just disappears when the property doesn't
      // exist or the user isn't authed. No red toast — this is header chrome.
    } finally {
      setLoading(false);
    }
  }, [propertyId]);
  useEffect(() => { load(); }, [load]);

  // Position dropdown under the anchor. Uses portal so nothing clips.
  useEffect(() => {
    if (!open || !anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + 6, left: rect.left });
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onDocClick = (e) => {
      // Ignore clicks inside the anchor pill or the portal menu.
      const menu = document.querySelector('[data-testid="strategy-picker-menu"]');
      if (menu?.contains(e.target)) return;
      if (anchorRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    document.addEventListener("mousedown", onDocClick);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
      document.removeEventListener("mousedown", onDocClick);
    };
  }, [open]);

  const eligible = useMemo(
    () => registry.filter((s) => s.deal_types.includes(dealType)),
    [registry, dealType],
  );

  const pick = async (key, inputs = {}) => {
    setSaving(key);
    try {
      const { data } = await api.patch(
        `/v3/properties/${propertyId}/strategy`,
        { key, inputs },
      );
      setActive(data.active_strategy);
      setOpen(false);
      toast.success(`Strategy set: ${data.active_strategy.label}`);
      onChange?.(data.active_strategy);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't set strategy");
    } finally {
      setSaving(null);
    }
  };

  if (!propertyId) return null;

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="strategy-picker-pill"
        className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 hover:bg-amber-100 px-2.5 py-1 text-[11.5px] font-semibold text-amber-900 transition-colors"
      >
        <Calculator className="w-3 h-3" />
        {loading ? (
          <span className="inline-flex items-center gap-1">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading…
          </span>
        ) : (
          <>
            <span data-testid="strategy-picker-label">
              {active?.label || "Pick strategy"}
            </span>
            {active?.max_offer && (
              <span className="text-amber-700 tabular-nums">
                &nbsp;·&nbsp;${Number(active.max_offer).toLocaleString()}
              </span>
            )}
          </>
        )}
        <ChevronDown className="w-3 h-3 opacity-70" />
      </button>

      {open && eligible.length > 0 && createPortal(
        <div
          role="menu"
          data-testid="strategy-picker-menu"
          className="z-[9999] fixed bg-white border border-slate-200 rounded-lg shadow-2xl py-1 min-w-[280px] max-w-[340px]"
          style={{ top: pos.top, left: pos.left }}
        >
          <div className="px-3 py-2 border-b border-slate-100">
            <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-black text-amber-800">
              <Sparkles className="w-3 h-3" /> Deal Strategy
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Swap the exit strategy and every offer number, Jarvis prompt,
              and pipeline stage recalibrates.
            </div>
          </div>
          <ul className="max-h-[320px] overflow-y-auto">
            {eligible.map((s) => {
              const inline = INLINE_DOLLAR_INPUT[s.key];
              if (inline) {
                return (
                  <li key={s.key}>
                    <InlineDollarRow
                      label={s.label}
                      testidKey={s.key}
                      isActive={active?.key === s.key}
                      saving={saving === s.key}
                      inline={inline}
                      onSubmit={(val) => pick(s.key, { [inline.field]: val })}
                    />
                  </li>
                );
              }
              return (
                <li key={s.key}>
                  <button
                    type="button"
                    disabled={saving === s.key}
                    onClick={() => pick(s.key)}
                    data-testid={`strategy-pick-${s.key}`}
                    className="w-full text-left px-3 py-2 text-[12.5px] hover:bg-amber-50 flex items-center gap-2 disabled:opacity-60"
                  >
                    {active?.key === s.key ? (
                      <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    ) : (
                      <span className="w-3.5 h-3.5 shrink-0" />
                    )}
                    <span className="font-semibold text-slate-800">{s.label}</span>
                    {saving === s.key && <Loader2 className="w-3 h-3 animate-spin ml-auto" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>,
        document.body,
      )}
    </>
  );
}


// ── Inline dollar-input row for strategies like fixed_profit that need
// a single number to run. Renders in the picker menu; the operator types
// and presses Enter (or clicks Save) to persist.
function InlineDollarRow({ label, testidKey, isActive, saving, inline, onSubmit }) {
  const [val, setVal] = useState(inline.default || "");
  const submit = () => {
    const n = Number(val);
    if (!Number.isFinite(n) || n <= 0) {
      toast.error("Enter a positive dollar amount");
      return;
    }
    onSubmit(n);
  };
  return (
    <div
      className={
        "px-3 py-2 text-[12.5px] flex flex-col gap-1.5 " +
        (isActive ? "bg-amber-50" : "hover:bg-amber-50/60")
      }
      data-testid={`strategy-row-${testidKey}`}
    >
      <div className="flex items-center gap-2">
        {isActive ? (
          <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
        ) : (
          <span className="w-3.5 h-3.5 shrink-0" />
        )}
        <span className="font-semibold text-slate-800">{label}</span>
      </div>
      <div className="text-[10.5px] text-slate-500 pl-5">{inline.hint}</div>
      <div className="pl-5 flex items-center gap-1.5">
        <div className="relative flex-1">
          <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
          <input
            type="number"
            inputMode="decimal"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            placeholder={inline.placeholder}
            data-testid={`strategy-pick-${testidKey}-input`}
            className="w-full text-[12px] tabular-nums border border-slate-200 rounded pl-6 pr-2 py-1 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-300"
          />
        </div>
        <button
          type="button"
          disabled={saving}
          onClick={submit}
          data-testid={`strategy-pick-${testidKey}`}
          className="text-[11px] font-bold text-white rounded px-2.5 py-1 disabled:opacity-60"
          style={{ backgroundImage: "linear-gradient(135deg, #6366F1, #4F46E5)" }}
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Apply"}
        </button>
      </div>
    </div>
  );
}
